// ============================================================================
//  PendingReviewPanel.js — slide-over that lists AI suggestions awaiting review.
// ----------------------------------------------------------------------------
//  AI never writes to the live library — only to condition_pending_suggestions.
//  A human reviews here, optionally edits the label / body / age scope, and
//  either accepts (promotes to live table) or rejects.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { templatePending, templatePendingAccept, templatePendingReject } from '../../BaseURL';
import notify from './notify';
import { IconCheck, IconClose, IconEdit } from './icons';

export default function PendingReviewPanel({
  conditionId, conditionName, initialCategory,
  onClose, onAccepted,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState(initialCategory || '');
  // { [pendingRowId]: { label, bodyTemplate, ageScope } } — only present while editing.
  const [drafts, setDrafts] = useState({});

  // ── Integration fix: lock the page behind the drawer ──────────────────────
  //  Added when this department moved into the TotalCare shell. The scrim is
  //  `position: fixed; inset: 0`, which spans the initial containing block —
  //  and that EXCLUDES the scrollbar. With the page behind still scrollable,
  //  its scrollbar stayed visible as a strip down the right of the drawer,
  //  and the wheel scrolled the page underneath instead of the queue.
  //
  //  Padding replaces the scrollbar's width so the layout does not jump
  //  sideways at the moment the drawer opens.
  useEffect(() => {
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding  = body.style.paddingRight;

    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templatePending(conditionId));
      setRows(data.rows || []);
    } catch (err) {
      notify.error('Failed to load queue', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [conditionId]);

  useEffect(() => { load(); }, [load]);

  const beginEdit = (row) => {
    const isNarrative = row.category === 'narratives';
    setDrafts((d) => ({
      ...d,
      [row.id]: isNarrative
        ? {
            label:       row.label || '',
            complaint:   row.body_template || '',
            history:     row.history_body || '',
            examination: row.examination_body || '',
            otherDocs:   row.other_docs_body || '',
          }
        : {
            label:        row.label || '',
            bodyTemplate: row.body_template || '',
          },
    }));
  };
  const cancelEdit = (id) => {
    setDrafts((d) => {
      const next = { ...d };
      delete next[id];
      return next;
    });
  };
  const patchDraft = (id, patch) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  };

  // Accept with (optional) reviewer overrides taken from the draft.
  const accept = async (row) => {
    setBusyId(row.id);
    try {
      const overrides = drafts[row.id] || {};
      await axios.post(templatePendingAccept(conditionId, row.id), overrides);
      notify.toast.success(`"${overrides.label || row.label}" added to ${row.category}`);
      cancelEdit(row.id);
      onAccepted?.();
      load();
    } catch (err) {
      notify.error('Accept failed', err?.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row) => {
    setBusyId(row.id);
    try {
      await axios.post(templatePendingReject(conditionId, row.id));
      notify.toast.info('Suggestion dismissed');
      cancelEdit(row.id);
      load();
    } catch (err) {
      notify.error('Reject failed', err?.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const filteredRows = filter ? rows.filter((r) => r.category === filter) : rows;
  const byCategory = filteredRows.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  const allCategories = Array.from(new Set(rows.map((r) => r.category)));

  return (
    <div className="tp-drawer-scrim" onClick={onClose}>
      <aside className="tp-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="tp-drawer-head">
          <div>
            <h3>AI review queue</h3>
            <p>{conditionName} — accept the good ones (edit first if you want), reject the rest.</p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </header>

        {allCategories.length > 1 && (
          <div className="tp-drawer-filter">
            <button
              type="button"
              className={`tp-drawer-chip ${filter === '' ? 'is-on' : ''}`}
              onClick={() => setFilter('')}
            >
              All ({rows.length})
            </button>
            {allCategories.map((c) => (
              <button
                key={c}
                type="button"
                className={`tp-drawer-chip ${filter === c ? 'is-on' : ''}`}
                onClick={() => setFilter(c)}
              >
                {c} ({rows.filter((r) => r.category === c).length})
              </button>
            ))}
          </div>
        )}

        <div className="tp-drawer-body">
          {loading && <div className="tp-empty">Loading queue…</div>}

          {!loading && filteredRows.length === 0 && (
            <div className="tp-empty">
              <h3>Queue is empty</h3>
              <p>Press "Suggest with AI" on any tab to fill this queue with candidate rows.</p>
            </div>
          )}

          {Object.entries(byCategory).map(([cat, items]) => (
            <section key={cat} className="tp-queue-section">
              <h4 className="tp-queue-cat">{cat} ({items.length})</h4>
              {items.map((r) => {
                const draft = drafts[r.id];
                const isEditing = !!draft;
                return (
                  <div key={r.id} className={`tp-queue-row ${isEditing ? 'is-editing' : ''}`}>
                    <div className="tp-queue-main">
                      {isEditing ? (
                        <PendingEditor
                          draft={draft}
                          isNarrative={r.category === 'narratives'}
                          onChange={(patch) => patchDraft(r.id, patch)}
                        />
                      ) : (
                        <>
                          <div className="tp-queue-label">
                            <strong>{r.label}</strong>
                          </div>
                          {r.category === 'narratives' ? (
                            <NarrativeSections row={r} />
                          ) : (
                            r.body_template && <div className="tp-queue-body">{r.body_template}</div>
                          )}
                          {r.rationale && <div className="tp-queue-rationale"><em>Why:</em> {r.rationale}</div>}
                          {r.source_hint && <div className="tp-queue-source">Source: {r.source_hint}</div>}
                        </>
                      )}
                    </div>

                    <div className="tp-queue-actions">
                      {!isEditing && (
                        <button className="tp-btn tp-btn-ghost tp-btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => beginEdit(r)}>
                          <IconEdit size={14} /> Edit
                        </button>
                      )}
                      <button className="tp-btn tp-btn-primary tp-btn-sm"
                              disabled={busyId === r.id}
                              onClick={() => accept(r)}>
                        <IconCheck size={14} /> {isEditing ? 'Save & Accept' : 'Accept'}
                      </button>
                      {isEditing ? (
                        <button className="tp-btn tp-btn-ghost tp-btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => cancelEdit(r.id)}>
                          Cancel
                        </button>
                      ) : (
                        <button className="tp-btn tp-btn-ghost tp-btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => reject(r)}>
                          Reject
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      </aside>
    </div>
  );
}

/* ── Read-only display of a narrative row's 4 sections ────────────────── */
function NarrativeSections({ row }) {
  const rows = [
    { label: 'Complaint',              value: row.body_template },
    { label: 'History',                value: row.history_body },
    { label: 'Physical Examination',   value: row.examination_body },
    { label: 'Other Documentation',    value: row.other_docs_body },
  ];
  return (
    <div className="tp-queue-narr">
      {rows.map((s) => (
        s.value ? (
          <div key={s.label} className="tp-queue-narr-section">
            <div className="tp-queue-narr-label">{s.label}</div>
            <div className="tp-queue-narr-body">{s.value}</div>
          </div>
        ) : null
      ))}
    </div>
  );
}

/* ── Inline edit form for one pending row ─────────────────────────────── */
// Age scope is not editable here — it inherits from the parent template.
function PendingEditor({ draft, isNarrative, onChange }) {
  if (isNarrative) {
    return (
      <div className="tp-pending-edit">
        <div className="tp-field">
          <label>Label</label>
          <input className="tp-input" value={draft.label}
            onChange={(e) => onChange({ label: e.target.value })} />
        </div>
        <div className="tp-field">
          <label>Complaint</label>
          <textarea className="tp-input" rows={3} value={draft.complaint}
            onChange={(e) => onChange({ complaint: e.target.value })} />
        </div>
        <div className="tp-field">
          <label>History</label>
          <textarea className="tp-input" rows={4} value={draft.history}
            onChange={(e) => onChange({ history: e.target.value })} />
        </div>
        <div className="tp-field">
          <label>Physical Examination</label>
          <textarea className="tp-input" rows={4} value={draft.examination}
            onChange={(e) => onChange({ examination: e.target.value })} />
        </div>
        <div className="tp-field">
          <label>Other Documentation</label>
          <textarea className="tp-input" rows={3} value={draft.otherDocs}
            onChange={(e) => onChange({ otherDocs: e.target.value })} />
        </div>
      </div>
    );
  }
  return (
    <div className="tp-pending-edit">
      <div className="tp-field">
        <label>Label</label>
        <input className="tp-input" value={draft.label}
          onChange={(e) => onChange({ label: e.target.value })} />
      </div>
      <div className="tp-field">
        <label>Body</label>
        <textarea className="tp-input" rows={5} value={draft.bodyTemplate}
          onChange={(e) => onChange({ bodyTemplate: e.target.value })} />
      </div>
    </div>
  );
}
